// Components/Tabs/OtherSettingsTab.jsx

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import AdminState from "../AdminState";
import OptionList from "../LabSettings/OptionList.jsx";
import DeleteLabTab from "./DeleteLabTab.jsx";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * OtherSettingsTab Component
 *
 * This component groups settings for a space, including renaming the lab,
 * toggling Parchment Digital Badges integration, and deleting the lab. It displays these options
 * using an OptionList component.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {string} props.badgrState - The current Badgr integration state ('True' or 'False').
 * @param {Object} [props.loadedSpace] - Current space loaded by the authorized admin route.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 * @param {number} props.index - The current index in the tab stack for proper stacking order.
 */
function OtherSettingsTab({ user, labId, badgrState, loadedSpace, pushTab, revert, index, onLabChanged }) {
  const [labName, setLabName] = useState(null);
  const [integrationEnabled, setIntegrationEnabled] = useState(badgrState === "True");
  const [skipCheckInPurpose, setSkipCheckInPurpose] = useState(false);
  const [savingCheckIn, setSavingCheckIn] = useState(false);
  const [skipToolSelection, setSkipToolSelection] = useState(false);
  const [requireToolSelection, setRequireToolSelection] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLabName(null);
    setLoadError(false);
    // The route refreshes this response on every entry. Standalone consumers
    // still load their own settings and can retry failed requests.
    const request = loadedSpace ? Promise.resolve(loadedSpace) : Backend.getLabById({ id: labId, UID: user.UID });
    request
      .then((res) => {
        if (res.status !== 200) throw new Error('load_failed');
        if (active) {
          setIntegrationEnabled(res.badgrIntegration ?? (badgrState === "True"));
          setSkipCheckInPurpose(Boolean(res.skipCheckInPurpose));
          setSkipToolSelection(Boolean(res.skipToolSelection) && res.requireToolSelection === false);
          setRequireToolSelection(res.requireToolSelection !== false);
          setLabName(res.name ?? "");
        }
      })
      .catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [labId, user.UID, badgrState, loadedSpace, retry]);

  useDocumentTitle("Admin: Space settings – Pinpoint");


  return (
    <Slider
      name="Space settings"
      navColor="rgb(178, 217, 217)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <div className="admin-settings-content">
      {loadError ? <AdminState status="error" title="Space settings could not be loaded"
        description="Try again to load the current settings."
        onRetry={() => setRetry(value => value + 1)} /> : labName === null ?
        <AdminState status="loading" title="Loading space settings…" /> : <OptionList
        elements={[
          {
            group: "General",
            visible: true,
            type: "input",
            name: "Rename space",
            default: labName,
            onClick: async (newName) => {
              const res = await Backend.editLab(user.UID, labId, newName, integrationEnabled);
              if (res.status === 200) {
                onLabChanged?.();
                return "Space renamed";
              }
              throw new Error("Could not rename the space. Try again.");
            },
          },
          {
            group: "Check-in",
            visible: true,
            type: "switch",
            name: "Skip check-in purpose",
            description: "Continue to tool selection without asking why the user is visiting. If no steps remain, check-in completes automatically.",
            value: skipCheckInPurpose,
            disabled: savingCheckIn,
            onClick: async (enabled) => {
              setSavingCheckIn(true);
              try {
                const res = await Backend.editLabSkipCheckInPurpose(labId, user.UID, enabled);
                if (res.status !== 200) throw new Error("Could not save the purpose setting. Try again.");
                setSkipCheckInPurpose(res.skipCheckInPurpose ?? enabled);
                onLabChanged?.();
                return "Purpose setting saved";
              } finally {
                setSavingCheckIn(false);
              }
            },
          },
          {
            visible: true,
            type: "switch",
            name: "Require tool selection",
            description: "Require a selection when tools are available to the user. Turn off to allow check-in without selecting tools.",
            value: requireToolSelection,
            disabled: savingCheckIn,
            onClick: async (enabled) => {
              setSavingCheckIn(true);
              try {
                const res = await Backend.editLabRequireToolSelection(labId, user.UID, enabled);
                if (res.status !== 200) throw new Error("Could not save the selection requirement. Try again.");
                setRequireToolSelection(res.requireToolSelection ?? enabled);
                setSkipToolSelection(enabled ? false : (res.skipToolSelection ?? skipToolSelection));
                onLabChanged?.();
                return "Selection requirement saved";
              } finally {
                setSavingCheckIn(false);
              }
            },
          },
          {
            visible: true,
            type: "switch",
            name: "Skip tool selection",
            description: "Skip the tool-selection step. No tool usage is recorded for these check-ins.",
            lockedReason: requireToolSelection ? "Require tool selection is on. Turn it off to skip this step." : undefined,
            value: skipToolSelection,
            disabled: requireToolSelection || savingCheckIn,
            onClick: async (enabled) => {
              setSavingCheckIn(true);
              try {
                const res = await Backend.editLabSkipToolSelection(labId, user.UID, enabled);
                if (res.status !== 200) throw new Error("Could not save the tool selection setting. Try again.");
                setSkipToolSelection(res.skipToolSelection ?? enabled);
                setRequireToolSelection(res.requireToolSelection ?? requireToolSelection);
                onLabChanged?.();
                return "Tool selection setting saved";
              } finally {
                setSavingCheckIn(false);
              }
            },
          },
          {
            group: "Integrations",
            visible: true,
            type: "switch",
            name: "Parchment Digital Badges integration",
            description: "Use Parchment Digital Badges to check whether users have the credentials required for tools in this space.",
            default: integrationEnabled,
            onClick: async (enabled) => {
              const res = await Backend.editLabBadgrIntegration(labId, user.UID, enabled);
              if (res.status !== 200) throw new Error("Could not save the integration setting. Try again.");
              setIntegrationEnabled(enabled);
              return "Saved";
            },
          },
          {
            group: "Space management",
            visible: user.isSuperAdmin,
            type: "button",
            name: "Delete space",
            onClick: () =>
              pushTab(
                <DeleteLabTab adminRoute="deleteSpace"
                  key={`deleteLab-${labId}`}
                  user={user}
                  labId={labId}
                  pushTab={pushTab}
                  revert={revert}
                  onLabChanged={onLabChanged}
                />
              ),
          },
        ]}
      />}
      </div>
    </Slider>
  );
}

// PropTypes for type-checking component props
OtherSettingsTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  badgrState: PropTypes.string.isRequired,
  loadedSpace: PropTypes.object,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onLabChanged: PropTypes.func,
};

export default OtherSettingsTab;
